import React, { useReducer, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'i18n'

import { BaseVideoConfig, CampaignConfig } from './config'
import { isRenderSupported } from './lib/engines'
import { MonitorSmartphoneIcon } from 'lucide-react'
import SplashStage from './components/SplashStage'
import VideoSelectionStage from './components/VideoSelectionStage'
import RecordStage from './components/RecordStage'
import PreviewStage from './components/PreviewStage'
import DownloadStage from './components/DownloadStage'
import EnginePicker from './components/EnginePicker'

import * as styles from './SideBySideVideo.module.scss'

type Step = 'splash' | 'selection' | 'record' | 'preview' | 'download'

type State = {
  step: Step
  selected: BaseVideoConfig | null
  userVideo: Blob | null
}

type Action =
  | { type: 'start' }
  | { type: 'select-video'; video: BaseVideoConfig }
  | { type: 'user-recorded'; userVideo: Blob }
  | { type: 'preview-next' }
  | { type: 'rerecord' }
  | { type: 'go'; step: Step }
  | { type: 'reset' }

const initial: State = {
  step: 'splash',
  selected: null,
  userVideo: null,
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'start':
      return { ...state, step: 'selection' }
    case 'select-video':
      return { ...state, selected: action.video, step: 'record' }
    case 'user-recorded':
      return { ...state, userVideo: action.userVideo, step: 'preview' }
    case 'preview-next':
      return { ...state, step: 'download' }
    case 'rerecord':
      return { ...state, userVideo: null, step: 'record' }
    case 'go':
      return { ...state, step: action.step }
    case 'reset':
      return initial
    default:
      return state
  }
}

export type TrackFn = (eventName: string, data?: Record<string, unknown>) => void

type Props = {
  config: CampaignConfig
  showEnginePicker?: boolean
  onDownloaded?: () => void
  onReady?: () => void
  nextStepButton?: React.ReactNode
  onEvent?: TrackFn
}

const noop: TrackFn = () => {}

const SideBySideVideoPage = ({
  config,
  showEnginePicker = false,
  onDownloaded,
  onReady,
  nextStepButton,
  onEvent = noop,
}: Props) => {
  const { t } = useTranslation('sideBySideVideo')
  const [state, dispatch] = useReducer(reducer, initial)

  // Capability gate. Render runs entirely client-side and needs at least WASM
  // bulk-memory (iOS 15+) or WebCodecs; iOS <= 14 can't even compile the core
  // wasm. Detect after mount (the checks touch window/WebAssembly, so SSR must
  // assume supported to avoid a hydration mismatch). The block is shown after
  // the splash, so unsupported devices still see the intro before being blocked.
  const [renderSupported, setRenderSupported] = useState(true)
  useEffect(() => {
    const ok = isRenderSupported()
    setRenderSupported(ok)
    if (!ok) onEvent('step_interaction_sbsv_unsupported')
    // run once on mount — onEvent identity may change but we only report once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onStart = useCallback(() => dispatch({ type: 'start' }), [])
  const onSelect = useCallback(
    (video: BaseVideoConfig) => dispatch({ type: 'select-video', video }),
    []
  )
  const onUserRecorded = useCallback(
    (userVideo: Blob) => dispatch({ type: 'user-recorded', userVideo }),
    []
  )
  const onPreviewNext = useCallback(() => dispatch({ type: 'preview-next' }), [])
  const onRerecord = useCallback(() => dispatch({ type: 'rerecord' }), [])
  const onReset = useCallback(() => dispatch({ type: 'reset' }), [])

  const isSplash = state.step === 'splash'
  const splashLayout = isSplash

  return (
    <div
      className={`${styles.root}${splashLayout ? ` ${styles.rootSplash}` : ''}`}
      style={{
        '--bg-primary': config.theme.bgPrimary,
        '--bg-secondary': config.theme.bgSecondary,
        '--bg-accent': config.theme.bgAccent,
        '--brand-primary': config.theme.brandPrimary,
        '--brand-primary-dark': config.theme.brandPrimaryDark,
        '--text-on-dark': config.theme.textOnDark,
        '--text-on-dark-muted': config.theme.textOnDarkMuted,
        '--text-on-light': config.theme.textOnLight,
        '--text-on-light-muted': config.theme.textOnLightMuted,
      } as React.CSSProperties}
    >
      {showEnginePicker && <EnginePicker />}
      {isSplash ? (
        <SplashStage config={config} onStart={onStart} onEvent={onEvent} />
      ) : !renderSupported ? (
        <div className={`${styles.card} ${styles.unsupported}`}>
          <MonitorSmartphoneIcon
            size={48}
            className={styles.unsupportedIcon}
            aria-hidden
          />
          <h2 className={styles.title}>
            {config.copy.unsupportedTitle || t('unsupported.title')}
          </h2>
          <p className={styles.subtitle}>
            {config.copy.unsupportedMessage || t('unsupported.message')}
          </p>
        </div>
      ) : (
        <>
        <div className={styles.card}>
          {state.step === 'selection' && (
            <VideoSelectionStage
              config={config}
              initialSelectedId={state.selected?.id}
              onBack={() => dispatch({ type: 'go', step: 'splash' })}
              onNext={onSelect}
              onEvent={onEvent}
            />
          )}

          {state.step === 'record' && state.selected && (
            <RecordStage
              config={config}
              selected={state.selected}
              onBack={() => dispatch({ type: 'go', step: 'selection' })}
              onComplete={onUserRecorded}
              onEvent={onEvent}
            />
          )}

          {state.step === 'preview' && state.selected && state.userVideo && (
            <PreviewStage
              config={config}
              selected={state.selected}
              userVideo={state.userVideo}
              onBack={() => dispatch({ type: 'go', step: 'record' })}
              onRerecord={onRerecord}
              onNext={onPreviewNext}
              onEvent={onEvent}
            />
          )}

          {state.step === 'download' && state.selected && state.userVideo && (
            <DownloadStage
              config={config}
              selected={state.selected}
              userVideo={state.userVideo}
              onBack={() => dispatch({ type: 'go', step: 'preview' })}
              onReset={onReset}
              onDownloaded={onDownloaded}
              onReady={onReady}
              nextStepButton={nextStepButton}
              onEvent={onEvent}
            />
          )}
        </div>
        {config.branding.footer && (
          <p
            className={styles.footer}
            dangerouslySetInnerHTML={{ __html: config.branding.footer }}
          />
        )}
        </>
      )}
    </div>
  )
}

export default SideBySideVideoPage
